<script setup lang="ts">
const config = useRuntimeConfig()
const tenant = useTenant()
// @ts-ignore
const isCypress = computed(() => window?.Cypress)

if (tenant.value.fontFamily) {
  useHead({
    link: [
      { rel: 'stylesheet', href: `https://fonts.googleapis.com/css2?family=${encodeURIComponent(tenant.value.fontFamily)}:wght@300;400;500;600;700&display=swap` }
    ],
    style: [
      { innerHTML: `:root { --app-font-family: '${tenant.value.fontFamily}', sans-serif; }` }
    ]
  })
}

// Per-tenant accent color → --app-accent (used by the `accent` Tailwind color
// on primary buttons, FAB, toggles). Falls back to the :root black default.
if (tenant.value.accentColor) {
  useHead({
    style: [
      { innerHTML: `:root { --app-accent: ${tenant.value.accentColor}; }` }
    ]
  })
}
</script>
<template>
  <NuxtLayout>
    <NuxtPage />
    <div
      v-if="config.public.debug && !isCypress"
      class="fixed p-2 bottom-0 w-full text-center text-xs text-stone-400 pointer-events-none"
    >
      <DebugInfo />
    </div>
  </NuxtLayout>
</template>
<style>
/* Default styles for the whole app */

:root {
  --app-font-family: 'Verdana', 'Geneva', sans-serif;
  --app-accent: #000000;

  /* Subtle lightness steps of the accent color (replace the neutral stone-*
     surface backgrounds). var() resolves lazily where used, so these pick up
     the per-tenant --app-accent injected via useHead. Accent = black (default)
     ⇒ neutral grays ≈ the previous stone look. Percentages kept low = subtle.
     The literal first declaration is a fallback for WebViews without color-mix. */
  --app-surface-100: #f5f5f4;
  --app-surface-100: color-mix(in srgb, var(--app-accent) 14%, white);
  --app-surface-200: #e7e5e4;
  --app-surface-200: color-mix(in srgb, var(--app-accent) 24%, white);
  --app-surface-300: #d6d3d1;
  --app-surface-300: color-mix(in srgb, var(--app-accent) 36%, white);
  --app-surface-400: #a8a29e;
  --app-surface-400: color-mix(in srgb, var(--app-accent) 55%, white);
}

body {
  @apply font-light;
  font-family: var(--app-font-family);
}

/* for slideshow */
body.noscroll {
  overscroll-behavior: none;
}

/* https://stackoverflow.com/questions/28411499/disable-scrolling-on-body */
html.noscroll,
body.noscroll {
  overflow: hidden;
  margin: 0;
  height: 100%;
}

.firebase-emulator-warning {
  visibility: hidden;
}

div {
  /* https://stackoverflow.com/questions/25704650/disable-blue-highlight-when-touch-press-object-with-cursorpointer */
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
}

h2 {
  @apply text-lg font-medium;
}

p {
  @apply my-4;
}

p a {
  @apply underline;
}

a {
  @apply cursor-pointer;
}

.txt-error {
  @apply text-red-500 font-medium text-sm;
}

.Vue-Toastification__container.custom-container-class {
  @apply p-5;
}

.Vue-Toastification__toast.custom-toast-class {
  @apply rounded-xl font-medium;
  font-family: var(--app-font-family);
}

.page-left-enter-active,
.page-right-enter-active,
.page-left-leave-active,
.page-right-leave-active {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  transition: all 150ms linear;
}
.page-left-enter-from,
.page-right-leave-to {
  transform: translateX(100%);
}
.page-left-leave-to,
.page-right-enter-from {
  transform: translateX(-100%);
}
.page-left-enter-to,
.page-right-enter-to {
  transform: translateX(0);
}

.page {
  min-height: 100vh;
  width: min(95vw, 700px);
  margin: 0 auto;
  padding: 1rem;
}
</style>
